import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Input from '../components/ui/Input';
import { iconsPath, imagePath } from '../utils/imagePath';
import { SidebarMenu } from '../components/ui/UtilsLayout';
import { Drawer } from '../components/ui/Drawer';
import { DocumentsLinks } from '../utils/constant';
import DocumentRightToc from '../components/DocumentRightToc';
import { documentationData } from '../components/data/data/Documentation/documentationData';
import { trandingTopics } from '../components/data/data/TrendingTopics/TrendingTopicsData';

/* ===================== MAIN PAGE ===================== */

const Documentation = () => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [tocOpen, setTocOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const observerRef = useRef(null);
  const searchRef = useRef(null);
  const tocRef = useRef(null);

  // Detect if this is a trending topic route: /documentation/trending/:id
  const trendingMatch = location.pathname.match(
    /^\/documentation\/trending\/(\d+)$/
  );
  const trendingId = trendingMatch ? Number(trendingMatch[1]) : null;
  const isTrending = trendingId !== null;
  const trendingTopic = isTrending ? trandingTopics[trendingId] : null;

  // Extract slug from URL pathname (only for non-trending routes)
  const slug = (() => {
    if (isTrending) return null;
    const path = location.pathname
      .replace('/documentation/', '')
      .replace('/documentation', '');
    return path || 'introduction';
  })();

  // Flatten the documentationData to include subsections
  const allSections = documentationData.flatMap((section) => [
    section,
    ...(section.subSections || []),
  ]);



  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const el = document.getElementById(location.state.scrollTo);
        const container = document.querySelector(
          '.flex-1.overflow-y-auto.scrollbar'
        );
        if (el && container) {
          const offset =
            el.getBoundingClientRect().top -
            container.getBoundingClientRect().top +
            container.scrollTop -
            100;
          container.scrollTo({ top: offset, behavior: 'smooth' });
        }
      }, 400); // wait for content to render
    }
  }, [location.state]);
  
  const currentSection = allSections.find((section) => section.slug === slug);
  const displaySection = currentSection || documentationData[0];

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isResultClick = event.target.closest('.search-result-item');
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        !isResultClick
      ) {
        setSearchResults([]);
        setSearchQuery('');
      }
      if (tocRef.current && !tocRef.current.contains(event.target)) {
        setTocOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close drawer on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setOpen(false);
        setTocOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll spy effect (only for documentation sections, not trending)
  useEffect(() => {
    window.scrollTo(0, 0);
    if (observerRef.current) observerRef.current.disconnect();

    const timer = setTimeout(() => {
      const scrollContainer = document.querySelector(
        '.flex-1.overflow-y-auto.scrollbar'
      );

      // Create observer INSIDE the timer, after DOM is ready
      const observerOptions = {
        root: scrollContainer,
        rootMargin: '-10% 0px -60% 0px',
        threshold: 0,
      };
      const observerCallback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      };

      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(
        observerCallback,
        observerOptions
      );

      if (isTrending && trendingTopic) {
        trendingTopic.steps?.forEach((_, i) => {
          const element = document.getElementById(`trending-step-${i}`);
          if (element) observerRef.current.observe(element);
        });
      } else {
        const sectionIds = [];
        if (displaySection?.slug) sectionIds.push(displaySection.slug);
        displaySection?.subSections?.forEach((sub) => {
          sectionIds.push(sub.title.toLowerCase().replace(/\s+/g, '-'));
        });
        sectionIds.forEach((id) => {
          const element = document.getElementById(id);
          if (element) observerRef.current.observe(element);
        });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [slug, displaySection, isTrending, trendingTopic]);
  // Search functionality
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.trim() !== '') setTocOpen(false);
    if (query.trim() === '') {
      setSearchResults([]);
      return;
    }

    const results = [];
    const queryLower = query.toLowerCase();

    documentationData.forEach((section) => {
      if (section.sectionTitle.toLowerCase().includes(queryLower)) {
        results.push({
          title: section.sectionTitle,
          slug: section.slug,
          type: 'section',
        });
      }
      section.blocks?.forEach((block) => {
        if (block.type === 'text') {
          const content = Array.isArray(block.content)
            ? block.content.join(' ')
            : block.content;
          if (content?.toLowerCase().includes(queryLower)) {
            const index = content.toLowerCase().indexOf(queryLower);
            const start = Math.max(0, index - 50);
            const end = Math.min(content.length, index + 100);
            const preview = content.substring(start, end);
            results.push({
              title: section.sectionTitle,
              slug: section.slug,
              type: 'content',
              preview:
                (start > 0 ? '...' : '') +
                preview +
                (end < content.length ? '...' : ''),
            });
          }
        }
        if (block.type === 'list') {
          block.items?.forEach((item) => {
            if (item.toLowerCase().includes(queryLower)) {
              results.push({
                title: section.sectionTitle,
                slug: section.slug,
                type: 'content',
                preview: item,
              });
            }
          });
        }
      });
      section.subSections?.forEach((sub) => {
        if (sub.title.toLowerCase().includes(queryLower)) {
          results.push({
            title: `${section.sectionTitle} → ${sub.title}`,
            slug: section.slug,
            subId: sub.title.toLowerCase().replace(/\s+/g, '-'),
            type: 'subsection',
          });
        }
        sub.blocks?.forEach((block) => {
          if (block.type === 'text') {
            const content = Array.isArray(block.content)
              ? block.content.join(' ')
              : block.content;
            if (content?.toLowerCase().includes(queryLower)) {
              const index = content.toLowerCase().indexOf(queryLower);
              const start = Math.max(0, index - 50);
              const end = Math.min(content.length, index + 100);
              const preview = content.substring(start, end);
              results.push({
                title: `${section.sectionTitle} → ${sub.title}`,
                slug: section.slug,
                subId: sub.title.toLowerCase().replace(/\s+/g, '-'),
                type: 'content',
                preview:
                  (start > 0 ? '...' : '') +
                  preview +
                  (end < content.length ? '...' : ''),
              });
            }
          }
        });
      });
    });

    // Also search trending topics
    trandingTopics.forEach((topic, i) => {
      if (topic.title.toLowerCase().includes(queryLower)) {
        results.push({
          title: topic.title,
          trendingId: i,
          type: 'trending',
        });
      }

      // Search inside steps
      topic.steps?.forEach((step) => {
        if (step.step?.toLowerCase().includes(queryLower)) {
          results.push({
            title: `${topic.title} → ${step.step}`,
            trendingId: i,
            type: 'trending',
            preview: step.step,
          });
        }

        step.bullets?.forEach((b) => {
          if (b.toLowerCase().includes(queryLower)) {
            results.push({
              title: topic.title,
              trendingId: i,
              type: 'trending',
              preview: b,
            });
          }
        });

        step.subSections?.forEach((sub) => {
          sub.points?.forEach((p) => {
            const text = typeof p === 'object' ? p.text : p;
            if (text?.toLowerCase().includes(queryLower)) {
              results.push({
                title: `${topic.title} → ${step.step}`,
                trendingId: i,
                type: 'trending',
                preview: text,
              });
            }
          });
        });
      });
    });

    const uniqueResults = results.filter(
      (result, index, self) =>
        index ===
        self.findIndex(
          (r) =>
            r.slug === result.slug &&
            r.title === result.title &&
            r.preview === result.preview
        )
    );
    setSearchResults(uniqueResults.slice(0, 10));
  };

  const handleResultClick = (result) => {
    setSearchQuery('');
    setSearchResults([]);
    setTocOpen(false);

    // Handle trending topic search results
    if (result.trendingId !== undefined) {
      navigate(`/documentation/trending/${result.trendingId}`);
      return;
    }

    const scrollToElement = (elementId) => {
      const element = document.getElementById(elementId);
      const contentContainer = document.querySelector(
        '.flex-1.overflow-y-auto.scrollbar'
      );
      if (element && contentContainer) {
        const elementRect = element.getBoundingClientRect();
        const containerRect = contentContainer.getBoundingClientRect();
        const scrollOffset =
          elementRect.top -
          containerRect.top +
          contentContainer.scrollTop -
          100;
        contentContainer.scrollTo({ top: scrollOffset, behavior: 'smooth' });
      }
    };

    if (result.subId) {
      if (result.slug === slug) {
        setTimeout(() => scrollToElement(result.subId), 50);
      } else {
        navigate(`/documentation/${result.slug}`);
        setTimeout(() => scrollToElement(result.subId), 350);
      }
    } else {
      if (result.slug !== slug) {
        navigate(`/documentation/${result.slug}`);
      } else {
        setTimeout(() => {
          const contentContainer = document.querySelector(
            '.flex-1.overflow-y-auto.scrollbar'
          );
          if (contentContainer)
            contentContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }, 50);
      }
    }
  };

  return (
    <div className="flex flex-col gap-6 mt-32 md:mt-40 text-white px-4 xl:px-8">
      <div className="absolute inset-x-0 md:top-40 h-[1px] bg-[#515151]" />
      <div className="w-full max-w-screen-xl mx-auto flex flex-col lg:flex-row gap-6 h-[calc(100vh-160px)]">
        {/* LEFT SIDEBAR */}
        <div className="hidden lg:block lg:w-1/4 pt-3  overflow-y-auto scrollbar">
          <SidebarMenu arr={DocumentsLinks} />
        </div>

        {/* MAIN CONTENT */}
        <div className="w-full lg:w-3/4 border-l border-[#515151] flex flex-col overflow-hidden">
          {/* STICKY HEADER SECTION */}
          <div className="sticky top-0 z-10 pt-12 md:pt-6 px-2 md:px-4 lg:px-6 pb-4">
            <div className="flex items-center justify-between gap-2 md:gap-4 mb-6">
              <div className="text-[#EE2B2A] text-2xl md:text-3xl lg:text-4xl font-bold font-jakarta flex-shrink-0">
                Documentation
              </div>
              <button
                onClick={() => setOpen(true)}
                className="lg:hidden flex-shrink-0"
              >
                <img
                  src={iconsPath.menuIcon}
                  alt="Open documentation menu"
                  className="h-5 w-5 md:h-6 md:w-6"
                />
              </button>
            </div>

            {/* Mobile TOC + Search */}
            <div ref={tocRef} className="md:block lg:hidden relative">
              <div className="flex items-stretch gap-3 max-w-2xl">
                {/* On This Page Button — hidden on trending pages */}
                {!isTrending && (
                  <button
                    onClick={() => setTocOpen(!tocOpen)}
                    className="w-[280px] md:w-[320px] flex items-center justify-between px-4 py-3 bg-[#1E1F22] border-[2px] border-[#535353] rounded-lg text-white"
                  >
                    <span className="font-medium text-sm md:text-base">
                      On This Page
                    </span>
                    <svg
                      className={`w-4 h-4 md:w-5 md:h-5 transition-transform ${tocOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                )}

                {/* Search Bar */}
                <div
                  ref={searchRef}
                  className="w-[280px] md:w-[320px] relative"
                >
                  <div className="relative h-full">
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-[#C1C1C1]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearch}
                      placeholder="Search Documentation.."
                      className="w-full h-full pl-10 md:pl-11 pr-3 py-3 bg-[#1E1F22] border-[2px] border-[#535353] rounded-lg text-white text-sm md:text-base placeholder:text-xs md:placeholder:text-sm placeholder:text-[#C1C1C1] focus:outline-none"
                    />
                  </div>
                  {searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-[#1E1F22] border-[2px] border-[#535353] rounded-lg max-h-96 overflow-y-auto homepage-scrollbar z-50 shadow-xl">
                      {searchResults.map((result, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleResultClick(result)}
                          className="search-result-item p-3 hover:bg-[#2A2B2E] border-b border-[#535353] last:border-b-0 cursor-pointer transition-colors"
                        >
                          <div className="text-white text-sm font-medium">
                            {result.title}
                          </div>
                          {result.preview && (
                            <div className="text-[#949494] text-xs mt-1 line-clamp-2">
                              {result.preview}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {tocOpen && displaySection && !isTrending && (
                <div
                  onClick={() => setTocOpen(false)}
                  className="absolute top-full left-0 mt-2 bg-[#1E1F22] border-[2px] border-[#535353] rounded-lg p-4 max-w-[20rem] z-50 shadow-xl"
                >
                  <DocumentRightToc
                    data={[displaySection]}
                    currentSlug={slug}
                    activeId={activeId}
                  />
                </div>
              )}
            </div>
          </div>

          {/* SCROLLABLE CONTENT AREA */}
          <div className="flex-1 overflow-y-auto scrollbar px-2 md:px-4 lg:px-6 pt-6 pb-10">
            {/* TRENDING TOPIC VIEW */}
            {isTrending ? (
              trendingTopic ? (
                <TrendingTopicContent topic={trendingTopic} />
              ) : (
                <div className="mt-10 text-center p-8 bg-red-900/20 border border-red-600 rounded">
                  <p className="text-xl text-red-400">
                    ❌ Trending topic not found
                  </p>
                </div>
              )
            ) : /* DOCUMENTATION SECTION VIEW */
            displaySection ? (
              <Section
                title={displaySection.sectionTitle}
                slug={displaySection.slug}
              >
                {displaySection.blocks && displaySection.blocks.length > 0 ? (
                  displaySection.blocks.map((block, j) => (
                    <BlockRenderer key={j} block={block} />
                  ))
                ) : (
                  <div className="p-4 bg-yellow-900/20 border border-yellow-600 rounded mt-4">
                    <p className="text-yellow-400">
                      ⚠️ No content blocks found for this section.
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      Make sure your documentationData has a blocks array for
                      slug: "{slug}"
                    </p>
                  </div>
                )}

                {displaySection.subSections?.map((sub, k) => (
                  <SubSection key={k} title={sub.title}>
                    {sub.blocks?.map((b, m) => (
                      <BlockRenderer key={m} block={b} />
                    ))}
                    {sub.image && (
                      <>
                        <img
                          src={imagePath[sub.image]}
                          alt={sub.imageCaption || ''}
                          className="w-full mt-4"
                        />
                        {sub.imageCaption && (
                          <p className="italic text-[#949494] pt-3">
                            {sub.imageCaption}
                          </p>
                        )}
                      </>
                    )}
                    {sub.moreInfo && <MoreInfo links={sub.moreInfo} />}
                  </SubSection>
                ))}
              </Section>
            ) : (
              <div className="mt-10 text-center p-8 bg-red-900/20 border border-red-600 rounded">
                <p className="text-xl text-red-400">❌ Section not found</p>
                <p className="text-gray-400 mt-2">Looking for slug: "{slug}"</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDEBAR - Desktop Only (hidden on trending pages) */}
        <div className="hidden lg:block lg:w-1/4 flex flex-col overflow-hidden">
          <div className="sticky top-0 z-10 pt-6 px-2 pb-4">
            <div ref={searchRef} className="relative">
              <div className="relative">
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C1C1C1]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Search Documentation"
                  className="w-full pl-12 pr-4 py-2 bg-[#1E1F22] border-[3px] border-[#535353] rounded-[50px] text-white text-base placeholder:text-xs placeholder:text-[#C1C1C1] focus:outline-none"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#1E1F22] border-[2px] border-[#535353] rounded-lg max-h-96 overflow-y-auto homepage-scrollbar z-50 shadow-xl">
                  {searchResults.map((result, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleResultClick(result)}
                      className="search-result-item p-3 hover:bg-[#2A2B2E] border-b border-[#535353] last:border-b-0 cursor-pointer transition-colors"
                    >
                      <div className="text-white text-sm font-medium">
                        {result.title}
                      </div>
                      {result.preview && (
                        <div className="text-[#949494] text-xs mt-1 line-clamp-2">
                          {result.preview}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar px-2">
            <DocumentRightToc
              data={isTrending ? null : [displaySection]}
              trendingTopic={isTrending ? trendingTopic : null}
              currentSlug={slug}
              activeId={activeId}
            />
          </div>
        </div>

        {/* MOBILE DRAWER */}
        <Drawer isOpen={open} setIsOpen={setOpen}>
          <SidebarMenu arr={DocumentsLinks} onClose={setOpen} />
        </Drawer>
      </div>
    </div>
  );
};

export default Documentation;

/* ===================== TRENDING TOPIC RENDERER ===================== */

const TrendingTopicContent = ({ topic }) => {
  return (
    <div className="scroll-mt-32">
      {/* Title */}
      <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-4 leading-snug">
        {topic.title}
      </h1>

      {/* Intro */}
      {topic.intro && (
        <p className="text-[#C1C1C1] text-base leading-relaxed mb-8 whitespace-pre-line">
          {topic.intro}
        </p>
      )}

      <div className="h-[1px] bg-[#515151] mb-8" />

      {/* Steps */}
      <div className="flex flex-col gap-10">
        {topic.steps?.map((stepObj, stepIdx) => (
          <TrendingStepBlock
            key={stepIdx}
            stepObj={stepObj}
            stepIdx={stepIdx}
          />
        ))}
      </div>
    </div>
  );
};

const TrendingStepBlock = ({ stepObj, stepIdx }) => {
  return (
    <div
      id={`trending-step-${stepIdx}`}
      className="flex flex-col gap-3 scroll-mt-32"
    >
      {/* Step heading */}
      {stepObj.step && (
        <h2 className="text-base md:text-lg font-semibold text-[#D8D8D8]">
          {stepObj.step}
        </h2>
      )}

      {/* Description */}
      {stepObj.description && (
        <p className="text-[#C1C1C1] text-sm md:text-base leading-relaxed">
          {stepObj.description}
        </p>
      )}

      {/* Bullets before image */}
      {stepObj.bullets?.length > 0 && (
        <ul className="list-decimal list-inside space-y-2 text-[#D8D8D8]">
          {stepObj.bullets.map((b, j) => (
            <li key={j} className="text-sm md:text-base leading-relaxed">
              {b}
            </li>
          ))}
        </ul>
      )}

      {/* Main image */}
      {stepObj.image && (
        <img
          src={stepObj.image}
          alt={stepObj.step || ''}
          className="w-full rounded-lg border-[1px] border-[#EE2B2A]"
        />
      )}

      {/* Bullets after image */}
      {stepObj.bulletsAfter?.length > 0 && (
        <div className="space-y-2 text-[#D8D8D8]">
          {stepObj.bulletsAfter.map((b, j) => (
            <p key={j} className="text-sm md:text-base leading-relaxed">
              {(stepObj.bullets?.length || 0) + j + 1}. {b}
            </p>
          ))}
        </div>
      )}
      {stepObj.imageAfter && (
        <img
          src={stepObj.imageAfter}
          alt={stepObj.step || ''}
          className="w-full rounded-lg border-[1px] border-[#EE2B2A] mt-2"
        />
      )}

      {/* SubSections */}
      {stepObj.subSections?.map((sub, k) => (
        <div key={k} className="mt-3 ">
          {sub.title && (
            <h3 className="text-sm md:text-base font-semibold text-white mb-2">
              {sub.title}
            </h3>
          )}

          {sub.points?.length > 0 && (
            <div className="space-y-2 text-[#D8D8D8]">
              {sub.bulleted === false ? (
                sub.points.map((p, l) => (
                  <div key={l}>
                    <p className="text-sm md:text-base leading-relaxed">
                      {typeof p === 'object' ? p.text : p}
                    </p>
                    {p?.subPoints?.length > 0 && (
                      <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-[#C1C1C1]">
                        {p.subPoints.map((sp, m) => (
                          <li key={m} className="text-sm leading-relaxed">
                            {sp}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              ) : (
                <ul className="list-disc list-inside space-y-1">
                  {sub.points.map((p, l) => (
                    <li
                      key={l}
                      className="text-sm md:text-base leading-relaxed"
                    >
                      {typeof p === 'object' ? p.text : p}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {sub.image && (
            <img
              src={sub.image}
              alt={sub.title || ''}
              className="w-full rounded-lg mt-3 border-[1px] border-[#EE2B2A]"
            />
          )}
          {sub.pointsAfter?.length > 0 && (
            <div className="space-y-2 mt-3 text-[#D8D8D8]">
              {sub.pointsAfter.map((p, l) => (
                <p key={l} className="text-sm md:text-base leading-relaxed">
                  {typeof p === 'object' ? p.text : p}
                </p>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ===================== DOCUMENTATION COMPONENTS ===================== */

const Section = ({ title, slug, children }) => {
  const id = slug || title.toLowerCase().replace(/\s+/g, '-');
  const isIntroduction = title.toLowerCase().includes('introduction');
  return (
    <div id={id} className="scroll-mt-32">
      <div
        className={`text-lg md:text-xl lg:text-2xl tracking-tight ${isIntroduction ? 'font-bold text-[#FFFFFF]' : 'font-Medium'}`}
      >
        {title}
      </div>
      <div className="font-extralight my-4 space-y-4">{children}</div>
    </div>
  );
};

const SubSection = ({ title, children }) => {
  const id = title.toLowerCase().replace(/\s+/g, '-');
  return (
    <div id={id} className="scroll-mt-32">
      <div className="text-xl font-bold text-[#D8D8D8] mb-2">{title}</div>
      <div className="font-light space-y-5">{children}</div>
    </div>
  );
};

const MoreInfo = ({ links }) => (
  <div className="mt-5">
    <div className="text-lg tracking-tight text-[#D8D8D8]">
      More information:
    </div>
    {links.map((link, i) => (
      <div
        key={i}
        className="cursor-pointer text-[#519FFF] underline underline-offset-4 pt-2"
      >
        {link}
      </div>
    ))}
  </div>
);

const BlockRenderer = ({ block }) => {
  if (!block || !block.type) return null;

  switch (block.type) {
    case 'text': {
      const renderText = (text) => {
        if (!text) return null;
        return text.split(/(\*\*.*?\*\*)/g).map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return (
              <span key={i} className="text-white font-medium">
                {part.slice(2, -2)}
              </span>
            );
          }
          return part;
        });
      };
      if (Array.isArray(block.content)) {
        return (
          <>
            {block.content.map((text, i) => (
              <p key={i} className={i > 0 ? 'mt-4' : ''}>
                {renderText(text)}
              </p>
            ))}
          </>
        );
      }
      return <p>{renderText(block.content)}</p>;
    }
    case 'highlightText': {
      return (
        <p>
          {block.content.map((item, i) => (
            <span
              key={i}
              className={item.highlight ? 'text-white font-medium' : ''}
            >
              {item.text}
            </span>
          ))}
        </p>
      );
    }
    case 'list': {
      const isDecimal = block.style === 'decimal';
      const isNone = block.style === 'none';
      const ListTag = isDecimal ? 'ol' : 'ul';
      return (
        <ListTag
          className={`pl-6 flex flex-col gap-2 ${isDecimal ? 'list-decimal' : isNone ? 'list-none' : 'list-disc'}`}
        >
          {block.items.map((li, i) => (
            <li key={i}>{renderBoldText(li)}</li>
          ))}
        </ListTag>
      );
    }
    case 'info': {
      return <div className="rounded-xl my-4">{block.content}</div>;
    }
    case 'image': {
      return (
        <img
          src={imagePath[block.src] || block.src}
          alt={block.alt || ''}
          className="w-full my-6 rounded-lg border-[1px] border-[#EE2B2A]"
        />
      );
    }
    default:
      return null;
  }
};

const renderBoldText = (text) => {
  if (!text) return null;
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <span key={index} className="font-bold text-white">
          {part.replace(/\*\*/g, '')}
        </span>
      );
    }
    return part;
  });
};
